import type { LocationQueryValue, LocationQuery, RouteLocationNormalizedGeneric } from 'vue-router';
import { useRoute, useState } from '#app';

/**
 * Converts a Vue Router query parameter to a string.
 *
 * This function is designed to handle the flexible nature of Vue Router query parameters,
 * which can be a single string, an array of strings, or null. It standardizes these values
 * to a single string, preferring the first element of an array if applicable, and providing
 * a default empty string if the value is null or undefined.
 *
 * @param {LocationQueryValue | LocationQueryValue[] | null} query - The query parameter to convert.
 * @returns The query parameter as a string. If the query is an array, the first element is returned.
 * If the query is null or undefined, an empty string is returned.
 *
 * @example
 * // For a single string query parameter
 * console.log(queryAsString('example')); // Output: 'example'
 *
 * @example
 * // For a query parameter array, returns the first element
 * console.log(queryAsString(['first', 'second'])); // Output: 'first'
 *
 * @example
 * // For a null or undefined query parameter, returns an empty string
 * console.log(queryAsString(null)); // Output: ''
 */
export function queryAsString(query: LocationQueryValue | LocationQueryValue[] | null | undefined) {
  if (Array.isArray(query)) return query[0] ?? ''; // Adds a fallback to '' if the array is empty
  if (query) return query;

  return '';
}

export default function useQueryParams(route?: RouteLocationNormalizedGeneric) {
  const routeToUse = route || useRoute();
  const { query } = routeToUse;
  const leadId = useState<LocationQueryValue | LocationQueryValue[] | null>('leadId', () => null);
  const otag = useState<string | null>('otag', () => null);
  const utmSource = useState<string | null>('utmSource', () => null);
  const queryParams = useState<LocationQuery | null>('query', () => null);
  const partnerToken = useState<string | null>('partnerToken', () => null);

  const isDM = computed(() => queryAsString(query.otag) === 'sp2kub');
  const isQRDM = computed(() => queryAsString(query.otag) === 'idtsfu');
  const isEM = computed(() => queryAsString(query.otag) === 'sa3ebe');
  const isEMDM = computed(() => isEM.value || isDM.value || isQRDM.value);

  // Function to update the state based on the current route
  const refreshTags = () => {
    const queryOtag = query.otag;
    const queryLeadId = query.lead_id;
    const queryUtmSource = query.utm_source;
    const queryLinkToken = query.linkToken;

    if (queryLeadId !== undefined) {
      leadId.value = queryLeadId;
    }

    if (queryOtag !== undefined) {
      otag.value = queryAsString(queryOtag);
    }

    if (queryUtmSource !== undefined) {
      utmSource.value = queryAsString(queryUtmSource);
    }

    if (partnerToken !== undefined) {
      partnerToken.value = queryAsString(queryLinkToken);
    }

    if (queryParams !== undefined) {
      queryParams.value = query;
    }
  };

  return { refreshTags, queryParams, leadId, otag, utmSource, partnerToken, queryAsString, isEMDM, isDM, isQRDM, isEM };
}
